/**
 * Document Printer Utility
 * Provides functions for printing Documents with configurable paper sizes
 * using an isolated invisible iframe or safe popup fallback.
 */
import { getPrinterSettings } from './receiptPrinter';

export interface PrintOptions {
  documentTitle?: string;
  autoClose?: boolean;
  paperSize?: string;
}

/**
 * Print element as an A4/configured document
 * @param element - HTMLElement to print
 * @param options - Print options
 */
export const printDocument = async (
  element: HTMLElement | null,
  options: PrintOptions = {}
): Promise<void> => {
  if (!element) {
    console.error('Print element not found');
    return;
  }

  const settings = getPrinterSettings();
  const {
    documentTitle = 'Clinical_Document',
    autoClose = settings.autoClose,
    paperSize = settings.paperSize || 'A4',
  } = options;

  return new Promise((resolve) => {
    try {
      // 1. Gather all link tags and style tags from current document
      const styleNodes: string[] = [];
      document.querySelectorAll('link[rel="stylesheet"]').forEach((link) => {
        styleNodes.push(link.outerHTML);
      });

      document.querySelectorAll('style').forEach((style) => {
        // Exclude styles with destructive print visibility rules
        const content = style.innerHTML || '';
        if (!content.includes('visibility: hidden') && !content.includes('body * {')) {
          styleNodes.push(style.outerHTML);
        }
      });

      const printCSS = `
        @page {
          size: ${paperSize} portrait;
          margin: 15mm 15mm 15mm 15mm;
        }

        *, *:before, *:after {
          box-sizing: border-box !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          visibility: visible !important;
        }

        html, body {
          margin: 0 !important;
          padding: 8px !important;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif !important;
          font-size: 13px !important;
          line-height: 1.5 !important;
          color: #1e293b !important;
          background: #ffffff !important;
          visibility: visible !important;
          width: 100% !important;
        }

        h1, h2, h3, h4, h5, h6 {
          margin-top: 0 !important;
          margin-bottom: 6px !important;
          color: #0f172a !important;
          font-weight: 700 !important;
        }

        p {
          margin-top: 0 !important;
          margin-bottom: 8px !important;
        }

        /* Ant Design Cards & Containers */
        .ant-card {
          background: #f8fafc !important;
          border: 1px solid #e2e8f0 !important;
          border-radius: 8px !important;
          margin-bottom: 16px !important;
        }

        .ant-card-body {
          padding: 12px 16px !important;
        }

        /* Tabular Data */
        table {
          width: 100% !important;
          border-collapse: collapse !important;
          margin: 12px 0 16px 0 !important;
          page-break-inside: auto !important;
        }

        th, td {
          padding: 8px 10px !important;
          vertical-align: middle !important;
        }

        th {
          background-color: #f1f5f9 !important;
          color: #475569 !important;
          font-weight: 600 !important;
          font-size: 11px !important;
          text-transform: uppercase !important;
          border-bottom: 2px solid #cbd5e1 !important;
        }

        tr {
          border-bottom: 1px solid #e2e8f0 !important;
          page-break-inside: avoid !important;
        }

        /* Status & Alert Tags */
        .ant-tag {
          display: inline-block !important;
          padding: 2px 8px !important;
          border-radius: 4px !important;
          font-size: 11px !important;
          font-weight: 600 !important;
          text-transform: uppercase !important;
          border: 1px solid #cbd5e1 !important;
        }

        .ant-divider {
          margin: 12px 0 !important;
          border-top: 1px solid #e2e8f0 !important;
        }

        /* Hide elements meant not to print */
        .no-print, button, .ant-btn {
          display: none !important;
        }
      `;

      const fullHTML = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <title>${documentTitle}</title>
            ${styleNodes.join('\n')}
            <style>
              ${printCSS}
            </style>
          </head>
          <body>
            ${element.outerHTML || element.innerHTML}
          </body>
        </html>
      `;

      // 2. Create isolated hidden iframe
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.style.visibility = 'hidden';
      iframe.setAttribute('title', documentTitle);
      document.body.appendChild(iframe);

      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (!iframeDoc) {
        throw new Error('Iframe document not accessible');
      }

      iframeDoc.open();
      iframeDoc.write(fullHTML);
      iframeDoc.close();

      const win = iframe.contentWindow;
      if (!win) {
        throw new Error('Iframe window not accessible');
      }

      const cleanup = () => {
        setTimeout(() => {
          try {
            if (iframe.parentNode) {
              document.body.removeChild(iframe);
            }
          } catch (e) {}
          resolve();
        }, 800);
      };

      // Wait a moment for styles, web fonts, and DOM to render inside the iframe
      setTimeout(() => {
        try {
          win.focus();
          win.onafterprint = cleanup;
          win.print();
          // Fallback cleanup if user cancels dialog
          setTimeout(cleanup, 4000);
        } catch (err) {
          console.error('Error invoking iframe print:', err);
          cleanup();
        }
      }, 250);

    } catch (err) {
      console.warn('Iframe print failed, falling back to popup window:', err);
      printViaPopupFallback(element, documentTitle, paperSize, autoClose).then(resolve);
    }
  });
};

/**
 * Fallback print using popup window with guaranteed inline script trigger
 */
const printViaPopupFallback = (
  element: HTMLElement,
  title: string,
  paperSize: string,
  autoClose: boolean
): Promise<void> => {
  return new Promise((resolve) => {
    const printWindow = window.open('', '_blank', 'width=900,height=800');
    if (!printWindow) {
      alert('Popup blocked. Please allow popups for printing.');
      resolve();
      return;
    }

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>${title}</title>
          <style>
            @page { size: ${paperSize} portrait; margin: 15mm; }
            * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; visibility: visible !important; }
            body { margin: 0; padding: 16px; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: #fff; color: #1e293b; visibility: visible !important; }
            table { width: 100%; border-collapse: collapse; margin: 12px 0; }
            th, td { padding: 8px 10px; border-bottom: 1px solid #e2e8f0; }
            th { background: #f8fafc; text-transform: uppercase; font-size: 11px; }
            .no-print { display: none !important; }
          </style>
        </head>
        <body>
          ${element.innerHTML}
          <script>
            function triggerPrint() {
              window.focus();
              setTimeout(function() {
                window.print();
                ${autoClose ? 'window.onafterprint = function() { window.close(); }; setTimeout(function() { window.close(); }, 3000);' : ''}
              }, 250);
            }
            if (document.readyState === 'complete' || document.readyState === 'interactive') {
              triggerPrint();
            } else {
              window.addEventListener('DOMContentLoaded', triggerPrint);
              window.addEventListener('load', triggerPrint);
            }
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
    resolve();
  });
};
